import {
  Alert,
  AlertSentiments,
  ButtonGroup,
  Label,
  Loading,
  Positions,
  Sentiments,
  Sizes,
  Variants,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  AlertCircle,
  Download,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { UK_TIMEZONE } from '@see-power-pft-common-js/utils/product-mapper';
import { useAuth } from 'common-react/auth/AuthProvider';
import { ShellModal } from 'components/Modal/ShellModal';
import { InfoCircleTooltip } from 'components/Tooltip/InfoCircleTooltip';
import {
  AVAILABILITY_REPORT_MODAL_DESCRIPTION,
  AVAILABILITY_REPORT_MODAL_TITLE,
  DATE_RANGE_END_OFFSET_DAYS,
  TimePeriodType,
} from 'constants/availability-report';
import { fromZonedTime } from 'date-fns-tz';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { DateTimeSPPicker } from 'pages/Availability/common/DateTimeSPPicker';
import { downloadData } from 'pages/Availability/common/Modals/downloadData';
import { ISODateStringUTC } from 'pages/Availability/types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(localeData);

interface AvailabilityDownloadModalProps {
  showModal: boolean;
  onClose: () => void;
  siteSourceKey: string;
}

const StyledButtonGroup = styled(ButtonGroup)`
  & label {
    width: 25%;
  }
`;

const DateTimePickerWrapper = styled.div`
  /* Target all direct children of this wrapper's children */
  & > div > div {
    display: flex !important;
    gap: 8px !important;
  }

  /* Make first element (date picker) take 2x space */
  & > div > div > * {
    &:nth-child(1) {
      flex: 2 1 0 !important;
      min-width: 0 !important;
    }

    /* Make last element (time select) take 1x space */
    &:nth-child(2) {
      flex: 1 1 0 !important;
      min-width: 0 !important;
      width: auto !important;
    }
  }
`;

const ResponsiveModalWrapper = styled.div`
  width: 600px;

  @media (max-width: 768px) {
    width: 90vw;
  }

  @media (max-width: 480px) {
    width: 95vw;
  }
`;

const Spacer = styled.div<{ size: number }>`
  height: ${(props) => props.size}px;
`;

type DownloadErrorAlert = {
  content: string;
  sentiment: AlertSentiments;
};

export const AvailabilityDownloadModal = ({
  showModal,
  onClose,
  siteSourceKey,
}: AvailabilityDownloadModalProps) => {
  const { authStoreAccess, signalAuthError } = useAuth();

  const [selectedTimePeriodType, setSelectedTimePeriodType] =
    useState<TimePeriodType>(TimePeriodType.Custom);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [fromDateTimeUTC, setFromDateTimeUTC] =
    useState<ISODateStringUTC | null>(null);
  const [toDateTimeUTC, setToDateTimeUTC] = useState<ISODateStringUTC | null>(
    null,
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<DownloadErrorAlert | undefined>(undefined);

  const handleDownload = async () => {
    setLoading(true);

    const fromDateTime = fromDateTimeUTC ? new Date(fromDateTimeUTC) : null;
    const toDateTime = toDateTimeUTC ? new Date(toDateTimeUTC) : null;

    if (!fromDateTime || !toDateTime) {
      setError({
        content: 'Please specify both start and end dates and times.',
        sentiment: Sentiments.Negative,
      });
      setLoading(false);
      return;
    }

    if (fromDateTime >= toDateTime) {
      setError({
        content: 'The start date must be before the end date.',
        sentiment: Sentiments.Negative,
      });
      setLoading(false);
      return;
    }

    setError(undefined);

    const result = await downloadData({
      getJwtToken: () => authStoreAccess.getJwtToken(),
      signalAuthError: async () => {
        await signalAuthError?.();
      },
      siteSourceKey: siteSourceKey,
      fromDate: fromDateTime,
      toDate: toDateTime,
    });

    setLoading(false);
    if (result.success) {
      onClose();
    } else {
      setError({
        content: result.error.message,
        sentiment: Sentiments.Negative,
      });
    }
  };

  const getModalWidth = () => {
    if (windowWidth <= 480) return Math.round(windowWidth * 0.95);
    if (windowWidth <= 768) return Math.round(windowWidth * 0.9);
    return 600;
  };

  const modalWidth = getModalWidth();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const updateDateRange = (start: Dayjs, end: Dayjs) => {
      // Convert to UTC start times (00:00 UK = 23:00 or 00:00 UTC depending on DST)
      const fromDateStr = start.format('YYYY-MM-DD');
      const toDateStr = end.format('YYYY-MM-DD');

      const fromUTC = fromZonedTime(`${fromDateStr}T00:00:00`, UK_TIMEZONE);
      const toUTC = fromZonedTime(`${toDateStr}T00:00:00`, UK_TIMEZONE);

      setFromDateTimeUTC(fromUTC.toISOString() as ISODateStringUTC);
      setToDateTimeUTC(toUTC.toISOString() as ISODateStringUTC);
    };

    const nowInUK = dayjs().tz(UK_TIMEZONE);

    const lastMonthStartDate = nowInUK.subtract(1, 'month').startOf('month');
    const lastMonthEndDate = nowInUK
      .subtract(1, 'month')
      .endOf('month')
      .add(DATE_RANGE_END_OFFSET_DAYS, 'day');

    const lastWeekStartDate = nowInUK.subtract(1, 'week').startOf('week');
    const lastWeekEndDate = nowInUK
      .subtract(1, 'week')
      .endOf('week')
      .add(DATE_RANGE_END_OFFSET_DAYS, 'day');

    const yesterdayStartDate = nowInUK.subtract(1, 'day');
    const yesterdayEndDate = nowInUK;

    switch (selectedTimePeriodType) {
      case TimePeriodType.LastMonth:
        updateDateRange(lastMonthStartDate, lastMonthEndDate);
        break;
      case TimePeriodType.LastWeek:
        updateDateRange(lastWeekStartDate, lastWeekEndDate);
        break;
      case TimePeriodType.Yesterday:
        updateDateRange(yesterdayStartDate, yesterdayEndDate);
        break;
      case TimePeriodType.Custom: {
        const today = nowInUK;
        updateDateRange(
          today.startOf('month'),
          today.endOf('month').add(DATE_RANGE_END_OFFSET_DAYS, 'day'),
        );
        break;
      }
      default:
        selectedTimePeriodType satisfies never;
    }
  }, [selectedTimePeriodType]);

  const downloadContent = (
    <>
      <Spacer size={12} />
      <StyledButtonGroup
        selectedKey={selectedTimePeriodType}
        type="single"
        size={Sizes.Small}
        onChange={(selectedValue) =>
          setSelectedTimePeriodType(selectedValue as TimePeriodType)
        }
        items={[
          { value: TimePeriodType.Custom, label: TimePeriodType.Custom },
          { value: TimePeriodType.Yesterday, label: TimePeriodType.Yesterday },
          { value: TimePeriodType.LastWeek, label: TimePeriodType.LastWeek },
          { value: TimePeriodType.LastMonth, label: TimePeriodType.LastMonth },
        ]}
        style={{ justifyContent: 'center', width: '100%' }}
      />
      <Spacer size={20} />

      <Label bold>From</Label>
      <Spacer size={5} />
      <DateTimePickerWrapper>
        <DateTimeSPPicker
          testId="download-modal-from-datetime"
          ISOStringUTCValue={fromDateTimeUTC}
          onChange={setFromDateTimeUTC}
          disableDate={selectedTimePeriodType !== TimePeriodType.Custom}
          disableTime={selectedTimePeriodType !== TimePeriodType.Custom}
        />
      </DateTimePickerWrapper>
      <Spacer size={20} />

      <Label bold>To</Label>
      <Spacer size={5} />
      <DateTimePickerWrapper>
        <DateTimeSPPicker
          testId="download-modal-to-datetime"
          ISOStringUTCValue={toDateTimeUTC}
          onChange={setToDateTimeUTC}
          disableDate={selectedTimePeriodType !== TimePeriodType.Custom}
          disableTime={selectedTimePeriodType !== TimePeriodType.Custom}
        />
      </DateTimePickerWrapper>
      <Spacer size={20} />

      {!!error ? (
        <>
          <Spacer size={10} />
          <Alert
            size={Sizes.Small}
            sentiment={error.sentiment}
            icon={<AlertCircle />}
            solidBgColor
            style={{ minHeight: 'unset', fontWeight: '300' }}
          >
            {error.content}
          </Alert>
        </>
      ) : (
        <Spacer size={20} />
      )}
    </>
  );

  const headerTitle = AVAILABILITY_REPORT_MODAL_TITLE;
  const headerDescription = AVAILABILITY_REPORT_MODAL_DESCRIPTION;

  return (
    <ResponsiveModalWrapper>
      <ShellModal
        title={headerTitle}
        headerDescription={headerDescription}
        titleAdornment={
          <InfoCircleTooltip
            tooltip="Availability values shown here reflect the figures used for trading and operational reporting. These may differ from values used for invoicing or contractual settlement calculations."
            infoCircleStyle={{ width: 16, height: 16 }}
            contentStyle={{ maxWidth: 360 }}
          />
        }
        onClose={onClose}
        open={showModal}
        scrollable
        zIndex={1000}
        data-testid="download-modal"
        actions={[
          {
            label: 'Cancel',
            props: {
              variant: Variants.Outlined,
              sentiment: Sentiments.Primary,
            },
            action: onClose,
          },
          {
            label: 'Download',
            props: {
              variant: Variants.Filled,
              sentiment: Sentiments.Primary,
              icon: loading ? <Loading /> : <Download />,
              iconPosition: Positions.Right,
            },
            action: handleDownload,
          },
        ]}
        showFooter
        loading={loading}
        size={Sizes.Large}
        minWidth={modalWidth}
        maxWidth={modalWidth}
      >
        {downloadContent}
      </ShellModal>
    </ResponsiveModalWrapper>
  );
};
